import { useFetchQuestion } from "~/hooks/learning-center/use-fetch-lms";
import { cn } from "~/lib/ui";

import { isAnswerGoal, resolveGoal } from "./questionnaire-logic";
import type {
  GoalData,
  GoalEntry,
  QuestionAnswer,
  QuestionData,
} from "./types";

/**
 * GoalAnswer — one selectable answer (SFCC-11290). Ported from composable
 * `goalAnswer.jsx`.
 *
 * Each answer's `result` is a bare reference to EITHER the next question or a
 * goal. Questions are lazy-fetched by id (`useFetchQuestion`); goals are resolved
 * from the pre-loaded `goals` list (no extra fetch). On select we branch:
 *   - question → append it and advance the stepper (`onSelectQuestion`)
 *   - goal     → set the recommended goal, truncating later questions (`onSelectGoal`)
 * Re-clicking the already-selected answer advances (`onAdvance`).
 */
interface GoalAnswerProps {
  answer: QuestionAnswer;
  answerId: number;
  selectedValue: string;
  goals: GoalEntry[];
  onSelectQuestion: (next: QuestionData) => void;
  onSelectGoal: (goal: GoalData) => void;
  onAdvance: () => void;
  setSelectedValue: (value: string) => void;
}

const GoalAnswer = ({
  answer,
  answerId,
  selectedValue,
  goals,
  onSelectQuestion,
  onSelectGoal,
  onAdvance,
  setSelectedValue,
}: GoalAnswerProps) => {
  const { result, answer: answerValue } = answer;
  const isGoal = isAnswerGoal(answer);

  // Lazy-fetch the next question only when this answer leads to one. Goals come
  // from the pre-loaded list, so no fetch for the goal branch.
  const { data: questionEntry } = useFetchQuestion(
    !isGoal ? result.id : undefined,
  );

  const resolveQuestion = (): QuestionData | undefined =>
    (result.value?.data as QuestionData | undefined) ??
    (questionEntry?.data as QuestionData | undefined);

  const determine = () => {
    if (isGoal) {
      const goal = resolveGoal(answer, goals);
      if (goal) onSelectGoal(goal);
      return;
    }
    const next = resolveQuestion();
    if (next) onSelectQuestion(next);
  };

  const handleSelect = () => {
    if (answerValue === selectedValue) {
      onAdvance();
      return;
    }
    setSelectedValue(answerValue);
    determine();
  };

  const selected = selectedValue === answerValue;

  return (
    <div
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      className={cn(
        "mb-4 w-full cursor-pointer rounded-md border border-solid border-neutralalpha-6 px-4 py-8 sm:w-[80%]",
        { "border-2 border-accent bg-accent/10": selected },
      )}
      onClick={handleSelect}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleSelect();
        }
      }}
      data-testid={`goalAnswer-${answerId}`}
    >
      <div className="flex items-center space-x-3">
        <span
          aria-hidden="true"
          className={cn(
            "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-neutralalpha-8",
            { "border-accent": selected },
          )}
        >
          {selected && <span className="h-2 w-2 rounded-full bg-accent" />}
        </span>
        <span className="text-sm">{answerValue}</span>
      </div>
    </div>
  );
};

export default GoalAnswer;
